import { useEffect, useRef } from 'react';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { shouldRefreshToken, getTimeUntilExpiration } from '@/utils/jwt';

/**
 * Hook to automatically refresh JWT token before expiration
 * Implements proactive token refresh for better UX and security
 */
export function useTokenRefresh() {
  const { token, refreshToken, setAuth, clearAuth, setRefreshToken } = useAuthStore();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  const refreshTokenIfNeeded = async () => {
    // Don't refresh if already refreshing or no tokens available
    if (isRefreshingRef.current || !token || !refreshToken) {
      return;
    }

    // Check if token needs refresh
    if (!shouldRefreshToken(token)) {
      scheduleNextRefresh();
      return;
    }

    try {
      isRefreshingRef.current = true;
      const response = await authService.refreshToken(refreshToken);
      
      // Update tokens in store and localStorage
      setAuth(response.token, response.refresh_token || refreshToken, null);
      
      // Schedule next refresh
      scheduleNextRefresh();
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, clear auth and redirect to login
      clearAuth();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } finally {
      isRefreshingRef.current = false;
    }
  };

  const scheduleNextRefresh = () => {
    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    if (!token) {
      return;
    }

    const timeUntilExpiration = getTimeUntilExpiration(token);
    if (!timeUntilExpiration || timeUntilExpiration <= 0) {
      // Token already expired or invalid, try to refresh immediately
      refreshTokenIfNeeded();
      return;
    }

    // Schedule refresh 5 minutes before expiration
    const refreshTime = Math.max(60000, timeUntilExpiration - 5 * 60 * 1000); // At least 1 minute

    refreshTimeoutRef.current = setTimeout(() => {
      refreshTokenIfNeeded();
    }, refreshTime);
  };

  useEffect(() => {
    if (token && refreshToken) {
      // Initial check and schedule
      refreshTokenIfNeeded();
    } else {
      // Clear timeout if no tokens
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [token, refreshToken]);

  return {
    refreshTokenIfNeeded,
  };
}
